import { decodeHeic, isHeic } from '../components/trips/share/decodeImageFile';
import { extractRawPreview, isRawPhoto } from './rawPreview';

/**
 * ADR-0054: the longest side the server keeps (`STORED_MAX_EDGE`). Shrinking
 * here too means a phone sends ~1 MB instead of ~8 MB per photo, and the
 * server never has to decode a 48 MP original.
 */
export const PHOTO_MAX_EDGE = 3000;
const JPEG_QUALITY = 0.9;

/** The fields `parseClientCapture` reads on the server (ADR-0042). */
export interface PhotoCaptureFields {
  /** "YYYY-MM-DDTHH:MM:SS", camera wall-clock, no zone. */
  takenAt?: string;
  latitude?: string;
  longitude?: string;
}

export interface PreparedPhoto {
  file: File;
  capture: PhotoCaptureFields;
}

/** A photo nothing can turn into an image: a RAW with no JPEG inside. */
export class UnreadablePhotoError extends Error {}

/** The size that fits within `max` keeping proportions, or null if it fits already. */
export function fitWithin(width: number, height: number, max: number): { width: number; height: number } | null {
  const scale = max / Math.max(width, height);
  if (scale >= 1) return null;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** EXIF "YYYY:MM:DD HH:MM:SS" and exifr's decimal GPS, as the server wants them. */
export function captureFields(rawDate: unknown, gps: { latitude?: number; longitude?: number } | null | undefined): PhotoCaptureFields {
  const match = typeof rawDate === 'string' ? /^(\d{4}):(\d{2}):(\d{2}) (\d{2}:\d{2}:\d{2})/.exec(rawDate) : null;
  const lat = gps?.latitude;
  const lng = gps?.longitude;
  const hasCoord = typeof lat === 'number' && Number.isFinite(lat) && typeof lng === 'number' && Number.isFinite(lng);
  return {
    ...(match ? { takenAt: `${match[1]}-${match[2]}-${match[3]}T${match[4]}` } : {}),
    ...(hasCoord ? { latitude: String(lat), longitude: String(lng) } : {}),
  };
}

// The canvas drops the EXIF, so the date and GPS that place the photo on its
// day and stop are read before, and travel as fields.
async function readCapture(file: File): Promise<PhotoCaptureFields> {
  try {
    const exifr = (await import('exifr')).default;
    const [tags, gps] = await Promise.all([
      exifr.parse(file, { pick: ['DateTimeOriginal', 'CreateDate'], reviveValues: false }).catch(() => null),
      exifr.gps(file).catch(() => null),
    ]);
    return captureFields(tags?.DateTimeOriginal ?? tags?.CreateDate, gps);
  } catch {
    return {};
  }
}

async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (e) {
    if (isHeic(file)) return decodeHeic(file);
    throw e;
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, type, JPEG_QUALITY));
}

/** The bitmap drawn at `size`, turned by an EXIF `orientation` (3, 6 or 8; cameras write no mirrors). */
async function render(
  bitmap: ImageBitmap,
  size: { width: number; height: number },
  orientation: number,
  type: string,
): Promise<Blob | null> {
  const quarterTurn = orientation === 6 || orientation === 8;
  const canvas = document.createElement('canvas');
  canvas.width = quarterTurn ? size.height : size.width;
  canvas.height = quarterTurn ? size.width : size.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  if (orientation === 3) { ctx.translate(canvas.width, canvas.height); ctx.rotate(Math.PI); }
  if (orientation === 6) { ctx.translate(canvas.width, 0); ctx.rotate(Math.PI / 2); }
  if (orientation === 8) { ctx.translate(0, canvas.height); ctx.rotate(-Math.PI / 2); }
  ctx.drawImage(bitmap, 0, 0, size.width, size.height);

  const blob = await toBlob(canvas, type);
  // iOS Safari keeps canvas memory until the canvas is emptied.
  canvas.width = 0;
  canvas.height = 0;
  return blob;
}

const jpegName = (name: string) => name.replace(/\.[^.]+$/, '') + '.jpg';

/** A smaller copy, or null when the original is already fine to send as is. */
async function shrink(file: File): Promise<File | null> {
  const bitmap = await decode(file);
  try {
    const size = fitWithin(bitmap.width, bitmap.height, PHOTO_MAX_EDGE);
    // A small HEIC still goes through: no browser but Safari can show it.
    if (!size && !isHeic(file)) return null;

    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await render(bitmap, size ?? { width: bitmap.width, height: bitmap.height }, 1, type);
    if (!blob) return null;
    const name = type === 'image/jpeg' ? jpegName(file.name) : file.name;
    return new File([blob], name, { type, lastModified: file.lastModified });
  } finally {
    bitmap.close();
  }
}

/** The JPEG a RAW carries inside, shrunk and turned upright. */
async function fromRaw(file: File): Promise<File> {
  const preview = await extractRawPreview(file).catch(() => null);
  if (!preview) throw new UnreadablePhotoError();
  // 'none': the turn comes from the RAW's EXIF, not from the JPEG's.
  const bitmap = await createImageBitmap(preview.jpeg, { imageOrientation: 'none' })
    .catch(() => { throw new UnreadablePhotoError(); });
  try {
    const size = fitWithin(bitmap.width, bitmap.height, PHOTO_MAX_EDGE) ?? { width: bitmap.width, height: bitmap.height };
    const blob = await render(bitmap, size, preview.orientation, 'image/jpeg');
    if (!blob) throw new UnreadablePhotoError();
    return new File([blob], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
  } finally {
    bitmap.close();
  }
}

/**
 * The photo ready to upload: shrunk to `PHOTO_MAX_EDGE` and with its capture
 * read. Anything the browser can't decode goes as it is — the server shrinks
 * it too, so this is the fast path, not the guarantee. A RAW is the exception:
 * the server can't read it either, so it throws `UnreadablePhotoError`.
 */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  const capture = await readCapture(file);
  if (isRawPhoto(file)) return { file: await fromRaw(file), capture };
  const shrunk = await shrink(file).catch(() => null);
  return { file: shrunk ?? file, capture };
}
